import type { HTMLAttributes, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { ButtonSystem } from '@/components/Buttons';
import { RECOMMENDATION_ROUTE } from '@/constants/routes';
import { useTranslation } from '@/utils/i18n';

/**
 * circle-empty-state — Discover on a circle where nobody has shared a sound
 * yet (Figma 3146-9436). Not shown on a failed load: that is not an empty
 * circle (`discoverDeckState`).
 *
 * "Make a reco" opens song search and nothing more: the share screen keeps its
 * own default circles (PO decision — the circle we came from is NOT preselected).
 */
export default function CircleEmptyState({
  children,
  ...rest
}: {
  /** Pull-to-refresh spinner, kept as a slot so this stays presentational. */
  children?: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      data-testid="discover-empty"
      className="tw:relative tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:items-center tw:overflow-y-auto tw:px-7 tw:pb-[var(--tab-bar-inset,0px)]"
      // An empty circle has no deck to pull on, so the state itself carries the gesture.
      {...rest}
    >
      {children}
      <div className="tw:my-auto tw:flex tw:min-h-[340px] tw:w-full tw:shrink-0 tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:rounded-sm tw:border tw:border-dark-1 tw:bg-dark-1/50 tw:px-4 tw:py-4 tw:text-center tw:text-white">
        <img
          src="/images/circle-empty.svg"
          alt=""
          aria-hidden="true"
          className="tw:h-24 tw:w-auto"
        />
        <div className="tw:text-base">
          <p className="tw:font-extrabold">{t('discover.empty.title')}</p>
          <p className="tw:mt-2">{t('discover.empty.text')}</p>
        </div>
        <ButtonSystem
          label={t('discover.empty.cta')}
          onClick={() => navigate({ to: RECOMMENDATION_ROUTE })}
          className="tw:w-auto tw:min-w-52"
          data-testid="discover-empty-reco"
        />
      </div>
    </div>
  );
}
